<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide">
    <q-card class="q-dialog-plugin change-date-dialog">
      <q-form @submit.prevent="changeDate">
        <q-card-section class="q-pb-sm">
          <div class="text-h6">Смена даты</div>
          <div class="text-caption text-grey-7 q-mt-xs">
            Выбрано задач: {{ tasks.length }}
          </div>
        </q-card-section>

        <q-card-section class="q-pt-sm q-gutter-sm">
          <q-input
            v-model="dateTime"
            filled
            label="Дата и время"
            :rules="[validDateRule]"
          >
            <template v-slot:prepend>
              <q-icon name="event" class="cursor-pointer">
                <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                  <q-date v-model="dateTime" mask="YYYY-MM-DD HH:mm">
                    <div class="row items-center justify-end">
                      <q-btn v-close-popup label="Закрыть" color="primary" flat />
                    </div>
                  </q-date>
                </q-popup-proxy>
              </q-icon>
            </template>
            <template v-slot:append>
              <q-icon name="access_time" class="cursor-pointer">
                <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                  <q-time v-model="dateTime" mask="YYYY-MM-DD HH:mm" format24h>
                    <div class="row items-center justify-end">
                      <q-btn v-close-popup label="Закрыть" color="primary" flat />
                    </div>
                  </q-time>
                </q-popup-proxy>
              </q-icon>
            </template>
          </q-input>

          <q-input
            v-model.number="latitude"
            type="number"
            filled
            label="Широта"
            :rules="[coordinatesPairRule, latitudeRangeRule]"
          />

          <q-input
            v-model.number="longitude"
            type="number"
            filled
            label="Долгота"
            :rules="[coordinatesPairRule, longitudeRangeRule]"
          />
        </q-card-section>

        <q-card-actions align="right">
          <q-btn :loading="loading" :disable="!canSubmit" no-caps label="Применить" type="submit" color="primary" />
          <q-btn :disable="loading" color="primary" label="Отмена" no-caps @click="onDialogCancel" />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import {computed, ref, toRefs} from 'vue'
import {useDialogPluginComponent, useQuasar} from 'quasar'
import {useRouteParams} from '@vueuse/router'
import {useEventBus} from '@vueuse/core'
import {taskKey} from '../util/events'
import {useDayJs} from '../composables/dayjs'
import {useChangeDatePerformTasks} from '../composables/performTask'

const props = defineProps<{
  tasks: number[]
}>()

const {tasks} = toRefs(props)
const $q = useQuasar()
const dayjs = useDayJs()
const projectId = useRouteParams('projectId', undefined, {transform: Number})
const taskEvent = useEventBus(taskKey)

const dateTime = ref<string | null>(null)
const latitude = ref<number | string | null>(null)
const longitude = ref<number | string | null>(null)
const loading = ref(false)

function isFilled(value: number | string | null) {
  return value !== null && value !== ''
}

function isValidLatitude(value: number | string | null) {
  if (!isFilled(value)) {
    return true
  }

  const coordinate = Number(value)
  return Number.isFinite(coordinate) && coordinate >= -90 && coordinate <= 90
}

function isValidLongitude(value: number | string | null) {
  if (!isFilled(value)) {
    return true
  }

  const coordinate = Number(value)
  return Number.isFinite(coordinate) && coordinate >= -180 && coordinate <= 180
}

const validDateRule = (value: string | null) => !value || dayjs(value).isValid() || 'Неверный формат даты'
const coordinatesPairRule = () => isFilled(latitude.value) === isFilled(longitude.value) || 'Укажите обе координаты'
const latitudeRangeRule = (value: number | string | null) => isValidLatitude(value) || 'Широта должна быть в диапазоне от -90 до 90'
const longitudeRangeRule = (value: number | string | null) => isValidLongitude(value) || 'Долгота должна быть в диапазоне от -180 до 180'

const canSubmit = computed(() => {
  const hasDate = !!dateTime.value
  const hasCoordinates = isFilled(latitude.value) && isFilled(longitude.value)

  return (hasDate || hasCoordinates)
    && (!hasDate || dayjs(dateTime.value).isValid())
    && isFilled(latitude.value) === isFilled(longitude.value)
    && isValidLatitude(latitude.value)
    && isValidLongitude(longitude.value)
})

const payload = computed(() => ({
  date: dateTime.value ? dayjs(dateTime.value).utc().format() : null,
  latitude: isFilled(latitude.value) ? Number(latitude.value) : null,
  longitude: isFilled(longitude.value) ? Number(longitude.value) : null
}))

const {execute} = useChangeDatePerformTasks(projectId, tasks, payload)

async function changeDate() {
  if (!canSubmit.value) {
    return
  }

  loading.value = true
  try {
    await execute(true)
    taskEvent.emit('refresh')
    $q.notify({type: 'positive', message: 'Дата и координаты обновлены', position: 'top-right'})
    onDialogOK()
  } finally {
    loading.value = false
  }
}

defineEmits([
  ...useDialogPluginComponent.emits
])

const {dialogRef, onDialogHide, onDialogOK, onDialogCancel} = useDialogPluginComponent()
</script>

<style scoped>
.change-date-dialog {
  width: min(560px, 95vw);
  max-width: 95vw;
}
</style>
